import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Plug, Key, Bell, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill, SectionHeader, SoftRule } from "@/components/dryvn";
// LANE 2 live-gating (2026-07-10): only Notifications has a working live
// detail page. Integrations + API are gated off (see those routes), and
// Profile / Appearance / Security / Plan & billing are self-referential
// dead-ends (they link back to this hub). Marked live:false so a stranger
// never taps a card that goes nowhere. The identity pills are fixtures.
import { IS_DEMO } from "@/lib/mode";

export const Route = createFileRoute("/more/settings/")({
  head: () => ({
    meta: [
      { title: "Settings — Dryvn Life" },
      {
        name: "description",
        content: "Preferences, integrations, and API access for your Dryvn Life account.",
      },
    ],
  }),
  component: SettingsHub,
});

// Dead cards (Profile/Appearance/Security/Plan & billing) are removed — they
// had no route and looped back to this hub (launch churn sweep, Lane 5b).
// `live: true` marks items that reach a working page in live mode; live:false
// items are hidden in live mode by the filter below (feature-gating, Lane 2).
const groups = [
  {
    label: "Connections",
    items: [
      {
        to: "/more/settings/integrations",
        label: "Integrations",
        desc: "Connect the services that feed Dryvn Life.",
        Icon: Plug,
        live: true,
      },
      {
        to: "/more/settings/api",
        label: "API & webhooks",
        desc: "Personal keys, webhooks, and developer access.",
        Icon: Key,
        badge: "2 keys",
        live: false,
      },
    ],
  },
  {
    label: "Account",
    items: [
      {
        to: "/more/settings/notifications",
        label: "Notifications",
        desc: "SMS, push, digest cadence, local suggestions.",
        Icon: Bell,
        live: true,
      },
    ],
  },
] as const;

function SettingsHub() {
  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-grey)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Settings"
        title={
          <>
            Everything,{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              tuned
            </em>
            .
          </>
        }
      >
        {IS_DEMO && (
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="life">
              <Sparkles className="mr-1 h-3 w-3" /> Family plan
            </Pill>
            <Pill tone="primary">Signed in as Sarah</Pill>
            <Pill tone="muted">sarah@dryvn.life</Pill>
          </div>
        )}
      </HeroCard>

      {groups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => IS_DEMO || item.live),
        }))
        .filter((group) => group.items.length > 0)
        .map((group, i, visibleGroups) => (
        <div key={group.label} className="mt-8">
          <SectionHeader title={group.label} eyebrow={`Section ${i + 1}`} />
          <div className="grid gap-3 sm:grid-cols-2">
            {group.items.map((item) => {
              const { to, label, desc, Icon } = item;
              const badge = "badge" in item ? item.badge : undefined;
              return (
                <Link key={label} to={to}>
                  <DCard className="flex items-center gap-3 transition-all hover:-translate-y-0.5">
                    <span
                      className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                      style={{
                        backgroundColor: "color-mix(in oklab, var(--dryvn-life) 30%, transparent)",
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: "var(--dryvn-primary-deep)" }} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                          {label}
                        </p>
                        {badge && <Pill tone="amber">{badge}</Pill>}
                      </div>
                      <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                        {desc}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4" style={{ color: "var(--dryvn-grey)" }} />
                  </DCard>
                </Link>
              );
            })}
          </div>
          {i < visibleGroups.length - 1 && <SoftRule />}
        </div>
      ))}
    </AppShell>
  );
}
